import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Avatar,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { Helmet } from "react-helmet-async";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { userSchema } from "../../utils/Schema";
import { userServices } from "../../services/AllServices";
import { IMAGE_BASE } from "../../utils/constants";

function AddEditUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    if (/^\d/.test(pastedText)) {
      e.preventDefault();
    }
  };

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await userServices.getById({ id });
        const userData = response?.data?.data;
        setData(userData);
        if (userData?.document) {
          setPreview(IMAGE_BASE + userData.document); // URL for image
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);


  const validateImage = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    const maxSizeInMB = 5;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (!file) {
      return { valid: false, message: "No file selected." };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, message: "Only JPG, PNG, or JPEH images are allowed." };
    }

    if (file.size > maxSizeInBytes) {
      return { valid: false, message: "File size must be less than 5MB." };
    }

    return { valid: true };
  };

  const validateStatus = (status) => {
    const validStatuses = ["notverified", "verified", "reject"];
    if (!status) {
      return "Please select a document review status.";
    }
  
    if (!validStatuses.includes(status)) {
      return "Invalid status selected.";
    }
  
    return null;  // If validation passes
  };
  


  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: userSchema,
    initialValues: {
      firstName: data?.firstName || "",
      lastName: data?.lastName || "",
      email: data?.email || "",
      phoneNumber: data?.phoneNumber || "",
      documentreview: data?.documentreview || "",
      document: data?.document || '',
    },
    onSubmit: async (values) => {
      setLoading(true);
      
      // Validate the document review status
      if(id){
      const statusError = validateStatus(values.documentreview);
      if (statusError) {
        // If there's an error, set the error in Formik
        formik.setFieldError("documentreview", statusError);
        setLoading(false);
        return;
      }}
      
      try {
        const formData = new FormData();
        if (id) formData.append("id", id);
        formData.append("firstName", values.firstName.trim());
        formData.append("lastName", values.lastName.trim());
        formData.append("email", values.email);
        formData.append("phoneNumber", values.phoneNumber);
        formData.append("imagePath", "document");
    
        if (id) formData.append("documentreview", values.documentreview);
        if (values.document) formData.append("document", values.document);
    
        const response = id
          ? await userServices.edit(formData)
          : await userServices.add(formData);
    
        toast.success(response?.data?.message, { toastId: "success" });
        navigate("/user-management");
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
    

    // onSubmit: async (values) => {
    //   setLoading(true);
    //   try {
    //     const formData = new FormData();
    //     if (id) formData.append("id", id);
    //     formData.append("firstName", values.firstName.trim());
    //     formData.append("lastName", values.lastName.trim());
    //     formData.append("email", values.email);
    //     formData.append("phoneNumber", values.phoneNumber);
    //     formData.append("imagePath", "document");

    //     if (id) formData.append("documentreview", values.documentreview);
    //     if (values.document) formData.append("document", values.document);

    //     const response = id
    //       ? await userServices.edit(formData)
    //       : await userServices.add(formData);

    //     toast.success(response?.data?.message, { toastId: "success" });
    //     navigate("/user-management");
    //   } catch (error) {
    //     errorHandler(error);
    //   } finally {
    //     setLoading(false);
    //   }
    // },
  });

  const { values, touched, errors, handleChange, handleSubmit, setFieldValue } = formik;

  return (
    <Box>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} User`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit} encType="multipart/form-data">
              <Grid container spacing={3}>
                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    required
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onPaste={handlePaste}
                    error={Boolean(touched.firstName && errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    inputProps={{ maxLength: 50 }}
                    onInput={(e) => (e.target.value = e.target.value.replace(/\s/g, ""))}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    required
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    error={Boolean(touched.lastName && errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                    inputProps={{ maxLength: 50 }}
                    onInput={(e) => (e.target.value = e.target.value.replace(/\s/g, ""))}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    required
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    required
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={handleChange}
                    error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>


                {/* Status Dropdown (Edit Only) */}
                {id && (
                  <Grid item xs={12} md={12}>
                    <FormControl fullWidth error={Boolean(touched.documentreview && errors.documentreview)}>
                      <InputLabel id="status-label">Document Review</InputLabel>
                      <Select
                        labelId="status-label"
                        id="documentreview"
                        name="documentreview"
                        value={values.documentreview}
                        label="Document Review"
                        onChange={handleChange}
                        error={Boolean(touched.documentreview && errors.documentreview)}
                      >
                        <MenuItem value="">Select Action</MenuItem>
                        <MenuItem value="notverified">Not verified</MenuItem>
                        <MenuItem value="verified">Accept</MenuItem>
                        <MenuItem value="reject">Reject</MenuItem>
                      </Select>

                      {touched.documentreview && errors.documentreview && (
                        <Box mt={1} color="error.main" fontSize={12}>
                          {errors.documentreview}
                        </Box>
                      )}
                    </FormControl>
                  </Grid>
                )}


                {/* Document Upload */}
                <Grid item xs={12} md={6}>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="upload-image"
                    type="file"
                    onChange={(e) => {
                      const file = e.currentTarget.files[0];
                      const validation = validateImage(file);

                      if (!validation.valid) {
                        toast.error(validation.message);
                        return;
                      }

                      setFieldValue("document", file);
                      setPreview(URL.createObjectURL(file));
                    }}

                  />
                  <label htmlFor="upload-image">
                    <LoadingButton
                      component="span"
                      startIcon={<PhotoCamera />}
                      variant="outlined"
                    >
                      Upload Document
                    </LoadingButton>
                  </label>
                  {preview && (
                    <Box mt={2}>
                      <Avatar
                        variant="rounded"
                        src={preview}
                        alt="Document Preview"
                        sx={{ width: 100, height: 100 }}
                      />
                    </Box>
                  )}
                  {touched.document && errors.document && (
                    <Box mt={1} color="error.main" fontSize={12}>
                      {errors.document}
                    </Box>
                  )}
                </Grid>


                <Grid item xs={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} User
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditUser;
